import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback } from 'react'

const useSetSearchParams = () => {
    const searchParams = useSearchParams()
    const { replace } = useRouter()
    const pathname = usePathname()

    const addParams = useCallback(
        (paramsObj: Record<string, string>) => {
            const params = new URLSearchParams(searchParams.toString())

            // Handle sort direction toggle
            if (paramsObj.sort_by) {
                const currentSortBy = params.get('sort_by')
                const currentOrder = params.get('order')

                if (currentSortBy === paramsObj.sort_by) {
                    // Toggle order between asc and desc
                    params.set('order', currentOrder === 'asc' ? 'desc' : 'asc')
                } else {
                    // Default to desc for new sort column
                    params.set('order', 'desc')
                }
            }

            // Set all params from the object
            for (const [key, value] of Object.entries(paramsObj)) {
                params.set(key, value)
            }

            const newPath = `${pathname}?${params.toString()}`
            const currentPath = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname

            // Only replace if the path has actually changed
            if (newPath !== currentPath) {
                replace(newPath, { scroll: false })
            }
        },
        [searchParams, pathname, replace]
    )

    const addParam = useCallback(
        (action: SearchParamsAction, operator: string) => {
            const params = new URLSearchParams(searchParams.toString())

            if (operator === '') {
                params.delete(action)
            } else {
                params.set(action, operator)
            }

            const newPath = `${pathname}?${params.toString()}`
            replace(newPath, { scroll: false })
        },
        [searchParams, pathname, replace]
    )

    const setParam = useCallback(
        (key: string, value: string) => {
            const params = new URLSearchParams(searchParams.toString())
            params.set(key, value)
            replace(`?${params.toString()}`)
        },
        [searchParams, replace]
    )

    const removeSpecificParam = useCallback(
        (keys: string[]) => {
            const params = new URLSearchParams(searchParams.toString())
            keys.forEach(key => params.delete(key))
            const query = params.toString()
            replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
        },
        [searchParams, pathname, replace]
    )

    const removeAndAddParams = useCallback(
        (removeActions: SearchParamsAction[], addParamsObj: Record<string, string>) => {
            const params = new URLSearchParams(searchParams.toString())
            removeActions.forEach(action => params.delete(action))
            Object.entries(addParamsObj).forEach(([key, value]) => params.set(key, value))
            const newPath = `${pathname}?${params.toString()}`
            replace(newPath, { scroll: false })
        },
        [searchParams, pathname, replace]
    )

    return { addParam, removeSpecificParam, removeAndAddParams, addParams, setParam }
}

export default useSetSearchParams
